import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { Analysis } from '../../constants/analysisTypes';
import BankStatementGapResults from '../../components/analysis/BankStatementGapResults';
import GLBankReconciliationResults from '../../components/analysis/GLBankReconciliationResults';
import CashActivityReportResults from '../../components/analysis/CashActivityReportResults';
import { fetchAnalysis, fetchCaseById } from '../../api/cases';

const ViewAnalysis: React.FC = () => {
  const { case_id, analysis_id } = useParams<{ case_id: string; analysis_id: string }>();
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [results, setResults] = useState<any | null>(null);
  const [statements, setStatements] = useState<any[] | null>(null);
  const [caseDetails, setCaseDetails] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();

  useEffect(() => {
    const loadAnalysis = async () => {
      try {
        setLoading(true);
        const [analysisResponse, caseResponse] = await Promise.all([
          fetchAnalysis(case_id!, analysis_id!, auth.user?.id_token),
          fetchCaseById(case_id!, auth.user?.id_token)
        ]);
        
        const { analysis, results, statements } = analysisResponse.data;
        setAnalysis(analysis);
        setResults(results);
        setStatements(statements);
        setCaseDetails(caseResponse.data);
      } catch (err) {
        console.error('Error loading analysis:', err);
        setError('Failed to load analysis details');
      } finally {
        setLoading(false);
      }
    };

    if (case_id && analysis_id && auth.user?.id_token) {
      loadAnalysis();
    }
  }, [case_id, analysis_id, auth.user?.id_token]);

  if (loading) {
    return <Spinner animation="border" />;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!analysis) {
    return <Alert variant="warning">Analysis not found</Alert>;
  }

  const BackButton = () => (
    <div className="mb-2">
      <Link to={`/case/${case_id}`} className="text-secondary text-decoration-none">
        ← Back to Case
      </Link>
    </div>
  );

  const renderResults = () => {
    if (analysis.status !== 'completed') {
      return <Alert variant="info">Analysis status: {analysis.status}</Alert>;
    }

    if (!results) {
      return <Alert variant="warning">No results available</Alert>;
    }

    switch (analysis.type) {
      case 'BANK_STATEMENT_GAP':
        return <BankStatementGapResults 
          results={results} 
          statements={statements || []} 
          caseNumber={caseDetails?.case_number} 
          caseTitle={caseDetails?.case_title}
          analysisName={analysis?.name}
          caseId={case_id}
        />;
      case 'GL_BANK_RECONCILIATION':
        return <GLBankReconciliationResults
          results={results}
          analysisName={analysis?.name}
          caseNumber={caseDetails?.case_number}
          caseTitle={caseDetails?.case_title}
          caseId={case_id}
        />;
      case 'CASH_ACTIVITY_REPORT':
        return <CashActivityReportResults
          results={results}
          analysisName={analysis?.name}
          caseNumber={caseDetails?.case_number}
          caseTitle={caseDetails?.case_title}
          caseId={case_id}
        />;
      default:
        return <Alert variant="warning">Unknown analysis type</Alert>;
    }
  };

  return (
    <div>
      <h2>{analysis.name}</h2>
      <p className="text-muted">{analysis.description}</p>
      <BackButton />
      {renderResults()}
    </div>
  );
};

export default ViewAnalysis; 